import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { BRAND_MINT } from '@/lib/utils'
import { getMyBankDetailsApi, type EmployerBankDetails } from '@/lib/billing'
import {
  LoaderIcon,
  BuildingIcon,
  ChevronLeftIcon,
  CopyIcon,
  CheckIcon,
  LandmarkIcon,
  CreditCardIcon,
  InfoIcon,
} from 'lucide-react'

export const Route = createFileRoute('/dashboard/billing/bank-details')({
  component: () => {
    const [bankDetails, setBankDetails] = useState<EmployerBankDetails | null>(null)
    const [loading, setLoading] = useState(true)
    const [copyField, setCopyField] = useState<string | null>(null)

    useEffect(() => {
      let cancelled = false
      async function load() {
        try {
          const res = await getMyBankDetailsApi()
          if (!cancelled) setBankDetails(res)
        } catch (err) {
          if (!cancelled) toast.error(err instanceof Error ? err.message : 'Failed to load bank details')
        } finally {
          if (!cancelled) setLoading(false)
        }
      }
      load()
      return () => { cancelled = true }
    }, [])

    const handleCopy = async (value: string, field: string) => {
      try {
        await navigator.clipboard.writeText(value)
        setCopyField(field)
        setTimeout(() => setCopyField(null), 2000)
      } catch { /* fallback */ }
    }

    return (
      <div className="mx-auto max-w-3xl">
        <div className="mb-6">
          <Link to="/dashboard/billing" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <ChevronLeftIcon className="size-3" />Back to billing
          </Link>
        </div>

        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
            <div>
              <h1 className="text-2xl font-semibold">Bank details</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Use these details to send wire transfers for your monthly invoices.
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading bank details…</p>
          </div>
        ) : !bankDetails ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
            <BuildingIcon className="size-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">No bank details configured</p>
            <p className="text-sm text-muted-foreground">Bank transfer details are not yet set up. Please check back later.</p>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="mb-6 flex items-center gap-4">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <LandmarkIcon className="size-6" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">PeerPass bank account</h2>
                <p className="text-xs text-muted-foreground">Send payments to this account</p>
              </div>
            </div>

            <div className="space-y-4">
              <CopyFieldDetail
                label="Bank name"
                value={bankDetails.bank_name}
                fieldKey="bank_name"
                copyField={copyField}
                onCopy={handleCopy}
              />
              <CopyFieldDetail
                label="Account holder"
                value={bankDetails.account_holder}
                fieldKey="account_holder"
                copyField={copyField}
                onCopy={handleCopy}
              />
              {bankDetails.account_number_masked && (
                <CopyFieldDetail
                  label="Account number"
                  value={bankDetails.account_number ?? bankDetails.account_number_masked ?? ''}
                  fieldKey="account_number"
                  copyField={copyField}
                  onCopy={handleCopy}
                />
              )}
              {bankDetails.iban_masked && (
                <CopyFieldDetail
                  label="IBAN"
                  value={bankDetails.iban ?? bankDetails.iban_masked ?? ''}
                  fieldKey="iban"
                  copyField={copyField}
                  onCopy={handleCopy}
                />
              )}
              {bankDetails.swift_bic && (
                <CopyFieldDetail
                  label="SWIFT/BIC"
                  value={bankDetails.swift_bic}
                  fieldKey="swift"
                  copyField={copyField}
                  onCopy={handleCopy}
                />
              )}
              {bankDetails.currency && (
                <div className="flex items-center gap-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <CreditCardIcon className="size-4" />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Currency</p>
                    <p className="text-sm font-medium text-foreground">{bankDetails.currency}</p>
                  </div>
                </div>
              )}
            </div>

            {bankDetails.notes && (
              <div className="mt-6 flex gap-3 rounded-lg bg-blue-500/10 px-4 py-3">
                <InfoIcon className="size-4 shrink-0 mt-0.5 text-blue-500" />
                <p className="text-xs text-blue-700 dark:text-blue-300">{bankDetails.notes}</p>
              </div>
            )}
          </div>
        )}
      </div>
    )
  },
})

function CopyFieldDetail({ label, value, fieldKey, copyField, onCopy }: {
  label: string; value: string; fieldKey: string; copyField: string | null; onCopy: (val: string, key: string) => void
}) {
  const isCopied = copyField === fieldKey
  return (
    <div className="flex items-center gap-3">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <LandmarkIcon className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">{label}</p>
        <div className="mt-0.5 flex items-center gap-1.5">
          <code className="flex-1 break-all rounded-md bg-muted/30 px-2 py-1 text-sm font-mono text-foreground">{value}</code>
          <button
            type="button"
            onClick={() => onCopy(value, fieldKey)}
            className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            title={`Copy ${label}`}
          >
            {isCopied ? <CheckIcon className="size-3.5 text-emerald-500" /> : <CopyIcon className="size-3.5" />}
          </button>
        </div>
      </div>
    </div>
  )
}
